import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';
import { periodLabelStyle, periodSelectStyle } from './periodSummaryUtils';

/**
 * Compact period summary table (Leads / Cotizaciones).
 * rows: [{ key?, label, tone: 'neutral'|'prospect'|'client'|'total', values: number[]|string[], indent?: number }]
 */
const PeriodSummaryTable = ({
  periods = [],
  rows = [],
  title = 'Resumen',
  collapsed = false,
  onToggleCollapsed,
  segmentOptions = [],
  segmentVisibility = {},
  onToggleSegment,
  onShowAllSegments,
  onHideAllSegments,
  segmentFilterLabel = 'Segmentos',
  metricOptions = [],
  metric,
  onMetricChange,
}) => {
  const ui = useUIColors();
  const border = ui.border;
  const headerBg = ui.header;
  const text = ui.text;
  const muted = ui.muted;
  const panel = ui.panel;
  const activeBg = `color-mix(in srgb, ${ui.interactive} 14%, ${panel})`;
  const rowBgByTone = {
    neutral: panel,
    prospect: `color-mix(in srgb, ${ui.warning} 14%, ${panel})`,
    client: `color-mix(in srgb, ${ui.success} 14%, ${panel})`,
    total: `color-mix(in srgb, ${ui.interactive} 12%, ${ui.section})`,
  };
  const cellPad = '4px 8px';
  const segmentMenuRef = useRef(null);
  const [segmentMenuOpen, setSegmentMenuOpen] = useState(false);

  useEffect(() => {
    if (!segmentMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (segmentMenuRef.current && !segmentMenuRef.current.contains(event.target)) {
        setSegmentMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSegmentMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [segmentMenuOpen]);

  const visibleSegmentCount = segmentOptions.filter(
    (segment) => segmentVisibility[segment] !== false
  ).length;
  const hasSegmentFilter = segmentOptions.length > 0 && typeof onToggleSegment === 'function';
  const hasMetricSelect = metricOptions.length > 0 && typeof onMetricChange === 'function';

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: '10px',
        overflow: 'hidden',
        margin: '10px 0 12px',
        background: panel,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          padding: '6px 10px',
          borderBottom: collapsed ? 'none' : `1px solid ${border}`,
          background: headerBg,
        }}
      >
        <h3 style={{ margin: 0, fontSize: '13px', color: text, fontWeight: 700, lineHeight: 1.2 }}>
          {title}
        </h3>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '8px',
            flexWrap: 'wrap',
            marginLeft: 'auto',
          }}
        >
          {hasSegmentFilter ? (
            <div ref={segmentMenuRef} style={{ position: 'relative', flex: '0 0 auto' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={periodLabelStyle(ui)}>{segmentFilterLabel}</span>
                <button
                  type="button"
                  onClick={() => setSegmentMenuOpen((prev) => !prev)}
                  style={{
                    ...periodSelectStyle(ui),
                    minWidth: '120px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '6px',
                  }}
                >
                  <span>
                    {visibleSegmentCount === segmentOptions.length
                      ? 'Todos'
                      : `${visibleSegmentCount} de ${segmentOptions.length}`}
                  </span>
                  <ChevronDown size={12} />
                </button>
              </label>
              {segmentMenuOpen ? (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    minWidth: '200px',
                    maxHeight: '280px',
                    overflowY: 'auto',
                    background: panel,
                    border: `1px solid ${border}`,
                    borderRadius: '8px',
                    boxShadow: ui.shadowMd || '0 8px 24px rgba(0,0,0,0.12)',
                    zIndex: 40,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '8px',
                      padding: '6px 10px',
                      borderBottom: `1px solid ${border}`,
                      background: headerBg,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => onShowAllSegments?.()}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: ui.interactive,
                        fontSize: '10px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      Todas
                    </button>
                    <button
                      type="button"
                      onClick={() => onHideAllSegments?.()}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: muted,
                        fontSize: '10px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      Ninguna
                    </button>
                  </div>
                  {segmentOptions.map((segment) => {
                    const checked = segmentVisibility[segment] !== false;
                    return (
                      <label
                        key={segment}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '7px 10px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: text,
                          cursor: 'pointer',
                          background: checked ? activeBg : panel,
                          borderBottom: `1px solid color-mix(in srgb, ${border} 70%, transparent)`,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggleSegment?.(segment)}
                          style={{ margin: 0, accentColor: ui.interactive }}
                        />
                        <span style={{ flex: 1 }}>{segment}</span>
                        {checked ? <Check size={14} color={ui.interactive} /> : null}
                      </label>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}

          {hasMetricSelect ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>Mostrar</span>
              <select
                value={metric}
                onChange={(event) => onMetricChange?.(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: '160px' }}
              >
                {metricOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {typeof onToggleCollapsed === 'function' ? (
            <button
              type="button"
              onClick={onToggleCollapsed}
              aria-expanded={!collapsed}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                border: `1px solid ${border}`,
                borderRadius: '6px',
                background: panel,
                color: text,
                fontSize: '10px',
                fontWeight: 600,
                padding: '0 8px',
                cursor: 'pointer',
                lineHeight: 1.2,
                height: '24px',
                alignSelf: 'flex-end',
              }}
            >
              {collapsed ? 'Mostrar' : 'Ocultar'}
              {collapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
            </button>
          ) : null}
        </div>
      </div>
      {!collapsed ? (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              minWidth: Math.max(680, 170 + periods.length * 88),
              borderCollapse: 'collapse',
            }}
          >
            <thead>
              <tr style={{ background: headerBg }}>
                <th
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    padding: cellPad,
                    textAlign: 'left',
                    fontSize: '10px',
                    color: muted,
                    borderRight: `1px solid ${border}`,
                    background: headerBg,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                  }}
                >
                  Métrica
                </th>
                {periods.map((period) => (
                  <th
                    key={period.key}
                    style={{
                      padding: cellPad,
                      textAlign: 'right',
                      fontSize: '10px',
                      color: muted,
                      borderLeft: `1px solid ${border}`,
                      whiteSpace: 'nowrap',
                      lineHeight: 1.2,
                    }}
                  >
                    {period.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                const indent = Number(row.indent || 0);
                const rowKey = row.key || row.label || `row-${rowIndex}`;
                return (
                <tr key={rowKey} style={{ background: rowBgByTone[row.tone] || panel }}>
                  <td
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      padding: cellPad,
                      paddingLeft: `${8 + indent * 14}px`,
                      borderTop: `1px solid ${border}`,
                      borderRight: `1px solid ${border}`,
                      background: rowBgByTone[row.tone] || panel,
                      color: indent > 0 ? muted : text,
                      fontSize: indent > 0 ? '10px' : '11px',
                      fontWeight: row.tone === 'total' ? 800 : indent > 0 ? 500 : 700,
                      whiteSpace: 'nowrap',
                      lineHeight: 1.2,
                    }}
                  >
                    {row.label}
                  </td>
                  {(row.values || []).map((value, index) => (
                    <td
                      key={`${rowKey}-${periods[index]?.key || index}`}
                      style={{
                        padding: cellPad,
                        borderTop: `1px solid ${border}`,
                        borderLeft: `1px solid ${border}`,
                        color: text,
                        fontSize: indent > 0 ? '10px' : '11px',
                        fontWeight: row.tone === 'total' ? 800 : indent > 0 ? 500 : 600,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.2,
                      }}
                    >
                      {value}
                    </td>
                  ))}
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default PeriodSummaryTable;
